import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  ChevronDown,
  Plus,
  MapPin,
  Phone,
  Trash,
  Trash2,
  Calendar,
  ChevronsRight,
  ChevronUp,
  ChevronDown as ChevronDownArrow,
} from "lucide-react";
import PhoneInput from "../../components/common/inputfields/PhoneInput";
import TextInput from "../../components/common/inputfields/TextInput";
import {
  selectSelectedDeliveryDate,
  setSelectedDeliveryDate,
} from "../../store/reducer/checkoutSlice";
import {
  setPartnerInfo,
  clearPartnerInfo,
  restoreFromSession,
  validateSession,
} from "../../store/reducer/deliveryPartnerSlice";
import { useDeliveryPartner } from "../../hooks/useDeliveryPartner";
import { useBuyAgainOrders } from "../../hooks/useBuyAgainOrders";
import { useDispatch, useSelector } from "react-redux";
import { updatePickupDate } from "../../store/thunk/userThunk";
import {
  fetchUserProfile,
  deliveryPartnerSignup,
} from "../../store/thunk/authThunk";
import { parseISO, format } from "date-fns";
import toast from "react-hot-toast";
import { OTPModal } from "../../shared/modal/AuthModal";
import { partnersCart, fetchCartInfo } from "../../store/thunk/cartThunk";
import DeliveryPartnerSubscriptionModal from "../../shared/modal/DeliveryPartnerSubscriptionModal";
import PastDateWarningModal from "../../shared/modal/PastDateWarningModal";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchDeliveryPartnerByShortName,
  fetchDeliveryPartnersList,
} from "../../store/thunk/menuThunk";
import { fetchCategoriesList } from "../../store/thunk/productThunk";
import PageLoader from "../../shared/loader/PageLoader";
import useIsMobile from "../../hooks/useIsMobile";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  filterValidDeliveryDates,
  validateDeliveryCutOffTime,
} from "../../utils/dateUtils";
import Select from "react-select";
import { customSelectStyles } from "../../hooks/commonFunction";

const DeliveryPartners = () => {
  const { alias } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Secure delivery partner hook
  const {
    partnerInfo: securePartnerInfo,
    userInfo: deliveryPartnerUserInfo,
    isValidSession,
    clearPartnerData,
    saveCustomNotes,
  } = useDeliveryPartner();

  const isMobile = useIsMobile();

  // Buy Again hook
  const { buyAgainOrders, buyAgainOrdersLoading } = useBuyAgainOrders();

  const [orderData, setOrderData] = useState({
    restaurant: "",
    address: "",
    phone: "",
    customerPhone: "",
    customerName: "",
    customerEmail: "",
    deliveryDate: "",
    products: [{ id: 1, product: "", quantity: 0, price: 0 }],
    customNotes: "",
    total: 0,
  });

  // States for checkout flow
  const [registeredPartnerData, setRegisteredPartnerData] = useState(null);
  const [otpVerificationData, setOtpVerificationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOTPModalOpen, setIsOTPModalOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState("initial"); // 'initial', 'otp-pending', 'completed'
  // Other existing states
  const [selectedDate, setSelectedDate] = useState("");
  const [lastFetchedPartnerId, setLastFetchedPartnerId] = useState(null);
  const [focusedSelectIndex, setFocusedSelectIndex] = useState(null);

  // Subscription modal states
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [selectedProductForSubscription, setSelectedProductForSubscription] =
    useState(null);

  // Past date warning modal state
  const [isPastDateWarningOpen, setIsPastDateWarningOpen] = useState(false);
  const [pastDateSelected, setPastDateSelected] = useState("");
  const [bypassPastDateCheck, setBypassPastDateCheck] = useState(null);

  // Redux selectors
  const { user, userInfo } = useSelector((state) => state.auth);
  const userRole = userInfo?.user_role;
  const { coreSettings } = useSelector((state) => state.auth);
  const deliveryDatesList = coreSettings?.PICKUP_DATE_LIST || [];
  const deliveryDates = filterValidDeliveryDates(deliveryDatesList || []);
  const selectedDeliveryDate = useSelector(selectSelectedDeliveryDate);
  // Menu slice selectors with safe fallbacks
  const {
    deliveryPartnersList = [],
    selectedDeliveryPartner,
    deliveryPartnersLoading,
    selectedDeliveryPartnerLoading,
  } = useSelector((state) => state.menus);
  // Product selectors
  const { categoriesList, categoriesListLoading: loadingProducts } = useSelector(
    (state) => state.products
  );
  const { cartData, loading: cartLoading } = useSelector((state) => state.cart);
  // Memoized values to prevent unnecessary re-renders
  const AllDeliveryPartnersList = useMemo(
    () => (Array.isArray(deliveryPartnersList) ? deliveryPartnersList : []),
    [deliveryPartnersList]
  );

  const productsArray = useMemo(
    () => (Array.isArray(categoriesList) ? categoriesList : []),
    [categoriesList]
  );

  // Helper function to find a product by ID in the grouped structure
  const findProductById = useCallback(
    (productId) => {
      for (const category of productsArray) {
        if (category.product_info && Array.isArray(category.product_info)) {
          const product = category.product_info.find(
            (p) => p.product_id === productId
          );
          if (product) return product;
        }
      }
      return null;
    },
    [productsArray]
  );

  const currentPartnerData = useMemo(
    () => selectedDeliveryPartner?.data || selectedDeliveryPartner,
    [selectedDeliveryPartner]
  );

  const partnerInfo = useMemo(() => {
    if (!currentPartnerData) return null;
    return {
      address: currentPartnerData?.address || "",
      phone: currentPartnerData.phone_number || "",
      email: currentPartnerData?.email || "",
    };
  }, [currentPartnerData]);

  // All your existing useEffects and handlers remain the same...
  useEffect(() => {
    dispatch(fetchDeliveryPartnersList());
    dispatch(fetchCartInfo());
    if (alias) {
      dispatch(fetchDeliveryPartnerByShortName(alias));
      if (user?.roles === 6) {
        const hasAccess =
          user?.partner_info?.includes(alias.toLowerCase()) ?? false;
        if (!hasAccess) {
          // redirect to first partner in partner_info
          const defaultPartner = user?.partner_info?.[0];
          if (defaultPartner) {
            navigate(`/delivery-partners/${defaultPartner}`, { replace: true });
          }
        }
      }
    }
  }, [dispatch, alias, user]);

  // Initialize custom notes from saved delivery partner user info
  useEffect(() => {
    if (deliveryPartnerUserInfo?.custom_notes) {
      setOrderData((prev) => ({
        ...prev,
        customNotes: deliveryPartnerUserInfo.custom_notes,
      }));
    }
  }, [deliveryPartnerUserInfo?.custom_notes]);

  // Reload products from cartData.item_info
  useEffect(() => {
    if (
      cartData?.order_type === "delivery-partner" &&
      cartData?.item_info &&
      Object.keys(cartData.item_info).length > 0
    ) {
      const loadedProducts = Object.values(cartData.item_info).map(
        (item, index) => ({
          id: index + 1,
          product: item.product_id,
          quantity: parseInt(item.product_quantity) || 1,
          price: parseFloat(item.product_price) || 0,
        })
      );

      setOrderData((prev) => ({
        ...prev,
        products:
          loadedProducts.length > 0
            ? loadedProducts
            : [{ id: 1, product: "", quantity: 0, price: 0 }],
      }));
    }
  }, [cartData?.item_info, cartData?.order_type]);

  // Set selectedDate from userInfo.pickup_date if not past
  useEffect(() => {
    if (userInfo?.pickup_date) {
      const pickupDate = new Date(userInfo.pickup_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (pickupDate >= today) {
        setSelectedDate(userInfo.pickup_date);
      }
    }
  }, [userInfo?.pickup_date]);

  // Validation function
  const validateFormData = useCallback(() => {
    const errors = [];

    if (
      !orderData.customerPhone ||
      orderData.customerPhone.replace(/\D/g, "").length !== 10
    ) {
      errors.push("Please enter a valid 10-digit phone number");
    }

    if (!orderData.customerName || !orderData.customerName.trim()) {
      errors.push("Please enter full name");
    }

    if (!selectedDeliveryDate) {
      errors.push("Please select a delivery date");
    }

    const validProducts = orderData.products.filter(
      (item) => item.product && item.quantity > 0
    );
    if (validProducts.length === 0) {
      errors.push("Please add at least one product with quantity");
    }

    if (!selectedDeliveryPartner) {
      errors.push("Please select a delivery partner");
    }

    return errors;
  }, [
    orderData.customerPhone,
    orderData.customerName,
    orderData.customerEmail,
    orderData.products,
    selectedDeliveryDate,
    selectedDeliveryPartner,
  ]);

  // Check if form is valid for enabling proceed button
  const isFormValid = useMemo(() => {
    return validateFormData().length === 0;
  }, [validateFormData]);

  // Check if partner accepts orders
  const isPartnerAcceptingOrders = currentPartnerData?.accept_order === 1;
  // Step 1: For role 6 users - direct partners-api call
  const handlePartnersApiCall = useCallback(async () => {
    try {
      const cleanedPhone = orderData.customerPhone.replace(/\D/g, "");
      const validProducts = orderData.products.filter(
        (item) => item.product && item.quantity > 0
      );

      // Build cart_info object
      const cart_info = {};
      validProducts.forEach((item) => {
        const product = findProductById(item.product);
        console.log("product", product);
        if (product && product?.product_id) {
          cart_info[product.product_id] = {
            quantity: item.quantity,
            enable_subscription: item.enable_subscription || false,
            ...(item.enable_subscription && {
              subscription_frequency: item.subscription_frequency || "weekly",
              subscription_custom_notes: item.subscription_custom_notes || "",
            }),
          };
        }
      });

      const partnersApiPayload = {
        user_id: String(deliveryPartnerUserInfo?.userId || ""),
        product_type: "delivery-partner",
        order_sub_type_id: String(currentPartnerData?.id),
        cart_info: cart_info,
      };
      const result = await dispatch(partnersCart(partnersApiPayload)).unwrap();

      if (result?.status === 1) {
        // Create mock partner data for consistent flow
        const partnerData = {
          user_id: result.user_id || Date.now(),
          phone_number: cleanedPhone,
          name: orderData.customerName,
          email: orderData.customerEmail,
        };

        return { success: true, needsOTP: false, userData: partnerData };
      } else {
        throw new Error(
          result?.message || "Partners API call failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Partners API call failed:", error);
      throw new Error(
        error.message || "Partners API call failed. Please try again."
      );
    }
  }, [
    orderData.customerPhone,
    orderData.customerEmail,
    orderData.customerName,
    orderData.products,
    productsArray,
    currentPartnerData,
    deliveryPartnerUserInfo,
    dispatch,
  ]);
  // Step 1: Register delivery partner
  const handleDeliveryPartnerSignup = useCallback(async () => {
    try {
      const cleanedPhone = orderData.customerPhone.replace(/\D/g, "");
      const signupPayload = {
        phone_number: cleanedPhone,
        name: orderData.customerName,
        phone_code: coreSettings?.DEFAULT_PHONE_CODE || "+1",
      };

      // Only include email if it's not empty
      if (orderData.customerEmail && orderData.customerEmail.trim()) {
        signupPayload.email = orderData.customerEmail;
      }
      const signupResult = await dispatch(
        deliveryPartnerSignup(signupPayload)
      ).unwrap();
      if (signupResult?.status === 1) {
        if (userRole === 6 || userRole === 1) {
          return signupResult?.data;
        } else {
          const userData = signupResult?.data;
          if (userData?.show_otp === 1) {
            // For other roles, OTP verification is required
            setOtpVerificationData({
              phone_number: userData?.phone_number,
              user_id: userData?.user_id,
              verification_type: userData?.verify_type || "register",
              mode: "partners-verify-otp",
              // Include partner information for secure storage
              partnerShortName: currentPartnerData?.short_name,
              partnerId: currentPartnerData?.id,
              partnerName: currentPartnerData?.name,
              partnerAddress: currentPartnerData?.address,
              userRole: userRole,
              phone_code: coreSettings?.DEFAULT_PHONE_CODE || "+1",
            });
            setCheckoutStep("otp-pending");
            setIsOTPModalOpen(true);
            return { success: true, needsOTP: true, userData };
          }
        }
      } else {
        throw new Error(
          signupResult?.message || "Registration failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Signup failed with error:", error);
      throw new Error(
        error.message || "Registration failed. Please try again."
      );
    }
  }, [
    orderData.customerPhone,
    orderData.customerEmail,
    orderData.customerName,
    dispatch,
    userRole,
  ]);

  // Step 3: Add to partners cart
  const handleAddToPartnerCart = useCallback(
    async (partnerId) => {
      try {
        const validProducts = orderData.products.filter(
          (item) => item.product && item.quantity > 0
        );

        // Build cart_info object
        const cart_info = {};
        validProducts.forEach((item) => {
          const product = findProductById(item.product);
          if (product && product?.product_id) {
            cart_info[product.product_id] = {
              quantity: item.quantity,
              enable_subscription: item.enable_subscription || false,
              ...(item.enable_subscription && {
                subscription_frequency: item.subscription_frequency || "weekly",
                subscription_custom_notes: item.subscription_custom_notes || "",
              }),
            };
          }
        });

        const cartPayload = {
          user_id: String(partnerId),
          product_type: "delivery-partner",
          order_sub_type_id: String(currentPartnerData?.id),
          cart_info: cart_info,
        };

        const cartResult = await dispatch(partnersCart(cartPayload)).unwrap();

        if (cartResult?.status === 1) {
          return true;
        } else {
          throw new Error(
            cartResult?.message || "Failed to add products to cart"
          );
        }
      } catch (error) {
        console.error("Cart addition error:", error);
        // Display API response error message if available
        const errorMessage =
          error?.message ||
          "Failed to add products to cart. Please try again.";

        throw new Error(errorMessage);
      }
    },
    [orderData.products, productsArray, currentPartnerData, dispatch]
  );

  // Main checkout handler
  const handleProceedToCheckout = useCallback(async () => {
    const validationErrors = validateFormData();

    if (validationErrors.length > 0) {
      toast.error(validationErrors[0]);
      return;
    }

    // Validate delivery cut-off time for non-role-6 users
    if (userInfo?.user_role !== 6 && userInfo?.user_role !== 1) {
      const validation = validateDeliveryCutOffTime(
        selectedDate,
        coreSettings?.PICKUP_DATE_LIST || []
      );

      if (!validation.isValid) {
        toast.error(validation.message);
        return;
      }
    }

    setLoading(true);

    try {
      // Check if phone number matches current user's phone
      const cleanedFormPhone = orderData.customerPhone.replace(/\D/g, "");
      const LoggedUserPhone = userInfo?.phone?.replace(/\D/g, "");

      if (userRole === 6 || userRole === 1) {
        //toast.loading('Processing order...', { id: 'checkout-process' });
        const existingData = await handleDeliveryPartnerSignup();
        await handlePartnersApiCall();
        // Fetch updated cart info after successful API call
        await dispatch(fetchCartInfo()).unwrap();
        toast.dismiss("checkout-process");
        //toast.success("Order processed successfully!");
        // Save delivery partner data securely to Redux + encrypted session
        dispatch(
          setPartnerInfo({
            partnerData: {
              shortName: currentPartnerData.short_name,
              partnerId: currentPartnerData.id,
              name: currentPartnerData.name,
              address: currentPartnerData.address,
            },
            userData: {
              userId: existingData?.user_id,
              userRole: userRole,
              phone: existingData?.phone_number,
            },
          })
        );

        // Navigate to order summary without state
        navigate(isMobile ? "/buy" : "/order-summary");
        return;
      } else if (cleanedFormPhone === LoggedUserPhone && userInfo?.phone) {
        //toast.success('Using existing customer information!');
        await handleAddToPartnerCart(userInfo?.user_id);
        await dispatch(fetchCartInfo()).unwrap();

        // Save delivery partner data securely to Redux + encrypted session
        dispatch(
          setPartnerInfo({
            partnerData: {
              shortName: currentPartnerData.short_name,
              partnerId: currentPartnerData.id,
              name: currentPartnerData.name,
              address: currentPartnerData.address,
            },
            userData: {
              userId: userInfo?.user_id,
              userRole: userRole,
              phone: userInfo?.phone,
            },
          })
        );

        // Navigate to order summary without state
        navigate(isMobile ? "/buy" : "/order-summary");
        return;
      } else {
        // Step 1: Register delivery partner for all roles
        const signupResult = await handleDeliveryPartnerSignup();
        if (signupResult.needsOTP && userRole !== 6) {
          // OTP verification required for non-role-6 users - modal will handle the rest
          toast("Please verify phone number.");
        } else {
          toast.success("Customer registered successfully!");
        }
      }
    } catch (error) {
      toast.dismiss("checkout-process");
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [
    validateFormData,
    registeredPartnerData,
    selectedDate,
    userRole,
    handlePartnersApiCall,
    handleDeliveryPartnerSignup,
    handleAddToPartnerCart,
    navigate,
  ]);

  // OTP verification success handler
  const handleOTPVerificationSuccess = useCallback(
    (savedPartnerData) => {
      setIsOTPModalOpen(false);
      setOtpVerificationData(null);

      if (savedPartnerData) {
        setRegisteredPartnerData(savedPartnerData);
        setCheckoutStep("completed");
      }

      // Use secure delivery partner data from the hook
      const userId =
        deliveryPartnerUserInfo?.userId || savedPartnerData?.user_id;

      if (!userId) {
        toast.error("User ID not found. Please try again.");
        return;
      }

      // Continue with cart addition after OTP verification
      handleAddToPartnerCart(userId)
        .then(async () => {
          // Fetch updated cart info after successful cart addition
          await dispatch(fetchCartInfo()).unwrap();
          //toast.success("Products added to cart successfully!");

          // Verify we have secure partner data, if not set it
          if (!isValidSession() || !securePartnerInfo) {
            dispatch(
              setPartnerInfo({
                partnerData: {
                  shortName: currentPartnerData.short_name,
                  partnerId: currentPartnerData.id,
                  name: currentPartnerData.name,
                  address: currentPartnerData.address,
                },
                userData: {
                  userId: userId,
                  userRole: userRole,
                  phone: orderData.customerPhone,
                },
              })
            );
          }

          navigate(isMobile ? "/buy" : "/order-summary");
        })
        .catch((error) => {
          toast.error(error.message);
        });
    },
    [
      handleAddToPartnerCart,
      navigate,
      securePartnerInfo,
      deliveryPartnerUserInfo,
      isValidSession,
      dispatch,
      currentPartnerData,
      userRole,
      orderData.customerPhone,
    ]
  );
  // Session validation and restoration on mount
  useEffect(() => {
    dispatch(restoreFromSession());
    dispatch(validateSession());
  }, [dispatch]);

  // Cleanup delivery partner data when component unmounts
  // useEffect(() => {
  //     return () => {
  //         if (!window.location.pathname.includes('/order-summary')) {
  //             dispatch(clearPartnerInfo());
  //         }
  //     };
  // }, [dispatch]);

  // Helper to check if selected date is a future date (not past)
  const isFutureDate = useMemo(() => {
    if (!selectedDeliveryDate?.date) return true; // Default to true if no date selected
    const selectedDateObj = new Date(selectedDeliveryDate.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDateObj.setHours(0, 0, 0, 0);
    return selectedDateObj >= today;
  }, [selectedDeliveryDate?.date]);

  // Track last fetch params to prevent duplicate API calls
  const lastFetchParamsRef = useRef(null);

  useEffect(() => {
    const partner = selectedDeliveryPartner?.data || selectedDeliveryPartner;
    // Past date + Role 6/1: inventory_checks = true
    // Future date + Role 6/1: inventory_checks = false
    // Other roles: inventory_checks = false
    const shouldCheckInventory = (userRole === 6 || userRole === 1) && !isFutureDate;
    // Include selectedDeliveryDate in fetchKey to refetch when date changes
    const fetchKey = `${partner?.id}-${selectedDeliveryDate?.date}-${shouldCheckInventory}`;

    if (
      partner?.id &&
      !loadingProducts &&
      lastFetchParamsRef.current !== fetchKey
    ) {
      lastFetchParamsRef.current = fetchKey;
      setLastFetchedPartnerId(partner.id);
      setOrderData((prev) => ({
        ...prev,
        products: [{ id: 1, product: "", quantity: 0, price: 0 }],
        total: 0,
      }));
      dispatch(
        fetchCategoriesList({
          order_sub_type_id: String(partner.id),
          status: 1,
          out_of_stock: "no",
          category_type: "delivery-partner",
          inventory_checks: shouldCheckInventory,
        })
      );
    }
  }, [
    dispatch,
    selectedDeliveryPartner,
    loadingProducts,
    isFutureDate,
    userRole,
    selectedDeliveryDate?.date,
  ]);

  useEffect(() => {
    if (selectedDeliveryPartner) {
      //console.log("selectedDeliveryPartner",selectedDeliveryPartner);
      const partner = selectedDeliveryPartner.data || selectedDeliveryPartner;
      setOrderData((prev) => ({
        ...prev,
        restaurant: partner.name || "",
        address: partner.address || "",
        phone: partner.phone_number || "",
      }));
    }
  }, [selectedDeliveryPartner]);

  // Set delivery date from userInfo (only when pickup_date changes)
  useEffect(() => {
    if (userInfo?.pickup_date && deliveryDates && deliveryDates.length > 0) {
      const matchingDate = deliveryDates.find(
        (date) => date.date === userInfo.pickup_date
      );
      if (matchingDate) {
        dispatch(setSelectedDeliveryDate(matchingDate));
      }
    }
  }, [userInfo?.pickup_date, deliveryDates, dispatch]);

  // Set customer info from userInfo for non-role-6 users (only when specific fields change)
  useEffect(() => {
    if (userInfo && userRole !== 6) {
      setOrderData((prev) => ({
        ...prev,
        customerPhone: userInfo?.phone || prev.customerPhone,
        customerEmail: userInfo?.email || prev.customerEmail,
        customerName: userInfo?.user_name || prev.customerName,
      }));
    }
  }, [userInfo?.phone, userInfo?.email, userInfo?.user_name, userRole]);

  // Track previous calculated total to prevent infinite loops
  const prevCalculatedTotalRef = useRef(0);

  const calculatedTotal = useMemo(() => {
    return orderData.products.reduce((sum, item) => {
      const product = findProductById(item.product);
      return sum + (product ? product.price * item.quantity : 0);
    }, 0);
  }, [orderData.products, productsArray, findProductById]);

  // Update total only when calculated total actually changes
  useEffect(() => {
    if (prevCalculatedTotalRef.current !== calculatedTotal) {
      prevCalculatedTotalRef.current = calculatedTotal;
      setOrderData((prev) => ({ ...prev, total: calculatedTotal }));
    }
  }, [calculatedTotal]);

  const handleDateSelect = useCallback(
    (date) => {
      // If user is authenticated, update via API
      if (userInfo?.user_id) {
        dispatch(
          updatePickupDate({
            user_id: userInfo.user_id,
            pickup_date: date?.date,
            pickup_time: "",
            delivery_method: "",
            action: "update-pickup-time",
          })
        ).then((resultAction) => {
          if (updatePickupDate.fulfilled.match(resultAction)) {
            setSelectedDate(date?.date);
            setOrderData((prev) => ({ ...prev, deliveryDate: date?.date }));
            //dispatch(fetchUserProfile());
          } else if (updatePickupDate.rejected.match(resultAction)) {
            const error =
              resultAction.payload ||
              resultAction.error.message ||
              "An unknown error occurred.";
            toast.error(error?.detail || "Failed to update delivery date");
          }
        });
      } else {
        // If user is not authenticated, only update local state
        setSelectedDate(date?.date);
        setOrderData((prev) => ({ ...prev, deliveryDate: date?.date }));
      }
    },
    [dispatch, userInfo?.user_id]
  );

  const handleDateSelection = useCallback(
    (dateOption) => {
      // Check if it's a past date BEFORE setting
      if (dateOption?.date) {
        const selectedDateObj = new Date(dateOption.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        selectedDateObj.setHours(0, 0, 0, 0);

        if (selectedDateObj < today) {
          // Show warning modal for past date
          setPastDateSelected(dateOption.label || dateOption.date);
          setIsPastDateWarningOpen(true);
          // Store the date option temporarily for later use if user proceeds
          setBypassPastDateCheck(dateOption);
          return; // Don't set the date yet
        }
      }

      // If it's not a past date, set it normally
      dispatch(setSelectedDeliveryDate(dateOption));
      handleDateSelect(dateOption);
    },
    [dispatch, handleDateSelect]
  );

  const handleDeliveryPartnerChange = useCallback(
    async (shortName) => {
      if (shortName && shortName !== alias) {
        setLoading(true); // Show loading when partner changes
        try {
          // Clear delivery date selection when partner changes
          dispatch(setSelectedDeliveryDate(null));
          setSelectedDate("");
          // Clear all cart data before changing partner
          setLoading(false);
          await clearPartnerData({ removeAllCart: true });
          navigate(`/delivery-partners/${shortName}`);
        } catch (error) {
          toast.error("Failed to change delivery partner");
          setLoading(false);
        }
      }
    },
    [alias, navigate, clearPartnerData, dispatch]
  );

  const handleProductChange = (index, field, value) => {
    setOrderData((prev) => {
      const updatedProducts = [...prev.products];
      if (field === "product") {
        if (value) {
          // Find the selected product to get its minimum_quantity
          const selectedProduct = findProductById(value);
          const minimumQuantity =
            Number(selectedProduct?.minimum_quantity) || 1;

          updatedProducts[index] = {
            ...updatedProducts[index],
            [field]: value,
            quantity: minimumQuantity,
            // Reset subscription when product changes
            enable_subscription: false,
          };
        } else {
          // Product cleared/removed - reset everything including subscription
          updatedProducts[index] = {
            ...updatedProducts[index],
            product: "",
            quantity: 0,
            enable_subscription: false,
          };
        }
      } else if (field === "quantity") {
        const numValue = parseInt(value) || 0;
        if (numValue === 0) {
          updatedProducts[index] = {
            ...updatedProducts[index],
            product: "",
            quantity: 0,
            // Reset subscription when quantity is 0
            enable_subscription: false,
          };
        } else {
          // Validate against min and max quantities
          const selectedProduct = findProductById(updatedProducts[index].product);
          const minimumQuantity =
            Number(selectedProduct?.minimum_quantity) || 1;
          const maxQuantity = Number(selectedProduct?.quantity) || 999;

          // Ensure quantity is within bounds
          const validQuantity = Math.max(
            minimumQuantity,
            Math.min(maxQuantity, numValue)
          );
          updatedProducts[index] = {
            ...updatedProducts[index],
            quantity: validQuantity,
          };
        }
      } else {
        updatedProducts[index] = {
          ...updatedProducts[index],
          [field]: value,
        };
      }

      return {
        ...prev,
        products: updatedProducts,
      };
    });
  };

  // Increment quantity function
  const incrementQuantity = (index) => {
    const currentProduct = orderData.products[index];
    if (currentProduct.product) {
      const selectedProduct = findProductById(currentProduct.product);
      const maxQuantity = Number(selectedProduct?.quantity) || 999;

      if (currentProduct.quantity < maxQuantity) {
        handleProductChange(index, "quantity", currentProduct.quantity + 1);
      }
    }
  };

  // Decrement quantity function
  const decrementQuantity = (index) => {
    const currentProduct = orderData.products[index];
    if (currentProduct.product) {
      const selectedProduct = findProductById(currentProduct.product);
      const minimumQuantity = Number(selectedProduct?.minimum_quantity) || 1;

      // If at minimum quantity, remove the product (if more than one product exists)
      if (currentProduct.quantity === minimumQuantity) {
        if (orderData.products.length > 1) {
          removeProduct(index);
        }
        // If only one product, do nothing (can't remove the last product)
      } else if (currentProduct.quantity > minimumQuantity) {
        handleProductChange(index, "quantity", currentProduct.quantity - 1);
      }
    }
  };

  const addProduct = useCallback(() => {
    setOrderData((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        { id: Date.now(), product: "", quantity: 0, price: 0 },
      ],
    }));
  }, []);

  const removeProduct = useCallback((index) => {
    setOrderData((prev) => {
      if (prev.products.length > 1) {
        const updatedProducts = prev.products.filter((_, i) => i !== index);
        return { ...prev, products: updatedProducts };
      }
      return prev;
    });
  }, []);

  const handleSubscribe = useCallback(
    (item) => {
      if (!item.product) return;

      const product = findProductById(item.product);
      if (product) {
        setSelectedProductForSubscription({ ...item, productDetails: product });
        setIsSubscriptionModalOpen(true);
      }
    },
    [findProductById]
  );

  // Handle subscription confirmation from modal
  const handleSubscriptionConfirm = useCallback(
    (subscriptionData) => {
      // Update the product in orderData with subscription info
      setOrderData((prev) => {
        const updatedProducts = prev.products.map((p) => {
          if (p.id === selectedProductForSubscription.id) {
            return {
              ...p,
              enable_subscription: true,
              subscription_frequency: subscriptionData.frequency,
              subscription_custom_notes: subscriptionData.customNotes,
            };
          }
          return p;
        });
        return { ...prev, products: updatedProducts };
      });

      toast.success("Subscription settings saved!");
      setIsSubscriptionModalOpen(false);
      setSelectedProductForSubscription(null);
    },
    [selectedProductForSubscription]
  );

  // Loading state for the entire component
  if (deliveryPartnersLoading && AllDeliveryPartnersList.length === 0) {
    return <PageLoader />;
  }

  // If no alias, show delivery partners list
  if (!alias) {
    return (
      <div className="bg-gray-50 py-5 lg:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto pb-20 md:pb-0">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-primary-100 text-white px-4 py-2">
              <h1 className="text-xl font-semibold letter-spacing-07">
                Select Delivery Partner
              </h1>
            </div>
            <div className="p-6">
              {deliveryPartnersLoading ? (
                <div className="text-center py-8">
                  <p>Loading delivery partners...</p>
                </div>
              ) : AllDeliveryPartnersList.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {AllDeliveryPartnersList.map((partner) => (
                    <div
                      key={partner.id}
                      onClick={() =>
                        navigate(`/delivery-partners/${partner.short_name}`)
                      }
                      className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors flex justify-between items-center group"
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg group-hover:text-primary-text transition-colors">
                          {partner.name}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {partner.address}
                        </p>
                        {partner?.formatted_phone_number && (
                          <p className="text-gray-600 text-sm mt-1">
                            {partner?.formatted_phone_number}
                          </p>
                        )}
                      </div>
                      <ChevronsRight className="text-gray-400 group-hover:text-primary-text h-5 w-5 ml-3 flex-shrink-0 transition-colors" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    No delivery partners available
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto pb-20 md:pb-0">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="bg-primary-100 text-white px-4 py-2">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-normal">Orders</h1>
                </div>
                <div className="relative">
                  <select
                    className="bg-primary-100 text-white px-4 py-2 rounded-md border-0 focus:outline-none appearance-none cursor-pointer pr-8 text-right"
                    value={alias}
                    onChange={(e) =>
                      handleDeliveryPartnerChange(e.target.value)
                    }
                    disabled={deliveryPartnersLoading}
                  >
                    <option value="" disabled className="text-left text-white">
                      {deliveryPartnersLoading
                        ? "Loading partners..."
                        : "Select Partner"}
                    </option>
                    {AllDeliveryPartnersList.map((partner) => (
                      <option
                        key={partner.id}
                        value={partner.short_name}
                        className="text-left"
                      >
                        {partner?.name || "Unknown Partner"}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none" />
                </div>
              </div>
            </div>

            <div className="p-2 md:p-4">
              {/* Partner Info Display */}
              {selectedDeliveryPartnerLoading ? (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg animate-pulse">
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                </div>
              ) : currentPartnerData ? (
                <div className="mb-5 rounded-lg">
                  <div className=" flex items-center space-x-2 justify-between">
                    {partnerInfo?.address && (
                      <div className="w-1/2 md:w-full flex items-start text-gray-600">
                        <MapPin className="w-4 h-4 mr-1 text-primary-100 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{partnerInfo.address}</span>
                      </div>
                    )}
                    {partnerInfo?.phone && (
                      <a
                        href={`tel:${partnerInfo.phone}`}
                        className="flex items-center text-gray-600 hover:text-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <Phone className="w-4 h-4 mr-1 text-primary-100 flex-shrink-0" />
                        <span className="text-sm hover:underline">
                          {partnerInfo.phone}
                        </span>
                      </a>
                    )}
                  </div>
                </div>
              ) : alias ? (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">
                    Partner "{alias}" not found. Please select a valid delivery
                    partner.
                  </p>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800 text-sm">
                    Please select a delivery partner to continue with
                    registration.
                  </p>
                </div>
              )}

              {/* Customer Info Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2 mb-6">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <PhoneInput
                    className="border-b border-gray-300 font-normal bg-transparent w-full"
                    value={orderData.customerPhone}
                    onChange={(value) =>
                      setOrderData((prev) => ({
                        ...prev,
                        customerPhone: value,
                      }))
                    }
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <TextInput
                    name="customerName"
                    type="text"
                    value={orderData.customerName}
                    onChange={(e) =>
                      setOrderData((prev) => ({
                        ...prev,
                        customerName: e.target.value,
                      }))
                    }
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <TextInput
                    name="customerEmail"
                    type="email"
                    value={orderData.customerEmail}
                    onChange={(e) =>
                      setOrderData((prev) => ({
                        ...prev,
                        customerEmail: e.target.value,
                      }))
                    }
                    required
                  />
                </div>

                {/* Delivery Date Selection */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delivery Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    {user?.roles === 6 || user?.roles === 1 ? (
                      // React DatePicker for role 6 and 1 - shows partner-specific dates including past dates with confirmation
                      <div className="relative custom-date-picker">
                        <DatePicker
                          selected={
                            selectedDeliveryDate?.date
                              ? parseISO(selectedDeliveryDate.date)
                              : null
                          }
                          onChange={(date) => {
                            if (date) {
                              const formattedDate = format(date, "yyyy-MM-dd");
                              const formattedLabel = format(
                                date,
                                "MMM dd, yyyy"
                              );
                              const selectedOption = {
                                date: formattedDate,
                                label: formattedLabel,
                              };
                              handleDateSelection(selectedOption);
                            }
                          }}
                          includeDates={(
                            selectedDeliveryPartner?.data?.delivery_dates || []
                          ).map((dateStr) => parseISO(dateStr))}
                          dateFormat="MMM dd, yyyy"
                          placeholderText="Select a date"
                          className="w-full px-0 py-2 pr-8 text-gray-900 bg-transparent border-0 border-b focus:outline-none focus:ring-0 border-gray-300 cursor-pointer"
                          wrapperClassName="w-full"
                        />
                        <Calendar className="absolute right-0 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                      </div>
                    ) : (
                      // Select dropdown for other roles
                      <select
                        name="deliveryDate"
                        value={selectedDate || ""}
                        onChange={(e) => {
                          setSelectedDate(e.target.value);
                          const selectedOption = deliveryDates.find(
                            (dateOption) => dateOption.date === e.target.value
                          );
                          if (selectedOption) {
                            handleDateSelection(selectedOption);
                          }
                        }}
                        className="w-full px-0 py-2 text-gray-900 bg-transparent border-0 border-b focus:outline-none focus:ring-0 border-gray-300"
                      >
                        <option value="" disabled>
                          Select a delivery date
                        </option>
                        {deliveryDates.map((dateOption) => (
                          <option key={dateOption.date} value={dateOption.date}>
                            {dateOption.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>

              {/* Products Section - same as before */}
              <div className="mb-6">
                <div className="bg-gray-100 px-2 md:px-4 py-4 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Products <span className="text-red-500">*</span>
                    </h3>
                    <button
                      onClick={addProduct}
                      className="flex items-center px-3 py-1 text-primary-100 border border-primary-100 rounded-md hover:bg-primary-100 hover:text-white transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Product
                    </button>
                  </div>
                  {orderData.products.map((item, index) => {
                    // Prepare Buy Again products group - HIDDEN (will reuse later)
                    // const buyAgainOptions = buyAgainOrders && buyAgainOrders.length > 0
                    //   ? [{
                    //       label: "Recent Orders",
                    //       options: buyAgainOrders
                    //         .filter((product) => {
                    //           return (
                    //             item.product === product.product_id ||
                    //             !orderData.products.some(
                    //               (p) =>
                    //                 p.product === product.product_id &&
                    //                 p.product !== ""
                    //             )
                    //           );
                    //         })
                    //         .map((product) => ({
                    //           value: product.product_id,
                    //           label:
                    //             product.weight &&
                    //             product.weight !== "0" &&
                    //             product.weight_symbol
                    //               ? `${product.weight} ${product.weight_symbol} x ${product.name} - ${product.formatted_price}`
                    //               : `1 x ${product.name} - ${product.formatted_price}`,
                    //         })),
                    //     }].filter((group) => group.options.length > 0)
                    //   : [];
                    const buyAgainOptions = [];

                    // Prepare regular category products
                    const categoryOptions = productsArray
                      .filter((category) => {
                        // Check if any product in this category is available
                        if (
                          !category.product_info ||
                          category.product_info.length === 0
                        ) {
                          return false;
                        }
                        // Include category if current item matches any product or if products are available
                        return category.product_info.some(
                          (product) =>
                            item.product === product.product_id ||
                            !orderData.products.some(
                              (p) =>
                                p.product === product.product_id &&
                                p.product !== ""
                            )
                        );
                      })
                      .map((category) => ({
                        label: category.name, // Category name as group label
                        options: category.product_info
                          .filter((product) => {
                            return (
                              item.product === product.product_id ||
                              !orderData.products.some(
                                (p) =>
                                  p.product === product.product_id &&
                                  p.product !== ""
                              )
                            );
                          })
                          .map((product) => ({
                            value: product.product_id,
                            label:
                              product.weight &&
                              product.weight !== "0" &&
                              product.weight_symbol
                                ? `${product.weight} ${product.weight_symbol} x ${product.name} - ${product.formatted_price}`
                                : `1 x ${product.name} - ${product.formatted_price}`,
                            // Show label only for past dates (for role 6/1)
                            showOutOfStockLabel: (userRole === 6 || userRole === 1) && !isFutureDate && Number(product.out_of_stock) == 1,
                            // Never disable selection (always allow selection)
                            isOutOfStock: false,
                          })),
                      }))
                      .filter((group) => group.options.length > 0); // Only include groups with available products

                    // Combine Buy Again and category options (Buy Again first)
                    const productOptions = [...buyAgainOptions, ...categoryOptions];

                    return (
                      <div
                        key={item.id}
                        className="relative gap-4 mb-4 px-2 md:px-4 py-2 bg-white rounded-lg border"
                      >
                        {/* Mobile: Stacked layout */}
                        <div className="md:hidden space-y-3">
                          {/* Row 1: Product Select (Full Width) */}
                          <div>
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              value={
                                productOptions
                                  .flatMap((group) => group.options)
                                  .find((opt) => opt.value === item.product) ||
                                null
                              }
                              onChange={(selectedOption) =>
                                handleProductChange(
                                  index,
                                  "product",
                                  selectedOption?.value || ""
                                )
                              }
                              onMenuOpen={() =>
                                setFocusedSelectIndex(`stacked-${index}`)
                              }
                              onMenuClose={() => setFocusedSelectIndex(null)}
                              options={productOptions}
                              placeholder={
                                focusedSelectIndex === `stacked-${index}`
                                  ? ""
                                  : "Select Product"
                              }
                              isClearable
                              isSearchable
                              name={`product-stacked-${index}`}
                              styles={customSelectStyles}
                              noOptionsMessage={() => "No products found"}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              formatOptionLabel={(option) => (
                                <div className="flex items-center gap-2 p-0">
                                  {option.showOutOfStockLabel && (
                                    <span className="text-primary-100 bg-red-100 font-semibold  text-[8px] leading-none px-1 py-1 rounded whitespace-nowrap">
                                      Out of <br/> Stock
                                    </span>
                                  )}
                                  <span className={option.isOutOfStock ? "text-gray-400" : ""}>
                                    {option.label}
                                  </span>
                                </div>
                              )}
                              isOptionDisabled={(option) => option.isOutOfStock}
                            />
                          </div>

                          {/* Row 2: Quantity and Price */}
                          <div className="grid grid-cols-3 gap-4">
                            {/* Quantity Input */}
                            <div className="flex items-center border border-neutral-300 rounded-full overflow-hidden w-fit bg-white">
                              {/* Minus Button - Show trash icon at minimum quantity */}
                              {item.quantity ===
                              (findProductById(item.product)
                                ?.minimum_quantity || 1) ? (
                                <button
                                  type="button"
                                  onClick={() => decrementQuantity(index)}
                                  disabled={
                                    item.quantity === 0 ||
                                    !item.product ||
                                    (item.quantity ===
                                      (findProductById(item.product)
                                        ?.minimum_quantity || 1) &&
                                      orderData.products.length === 1)
                                  }
                                  className="w-8 h-8 text-xl font-bold disabled:opacity-40 flex items-center justify-center"
                                >
                                  <Trash2 size={16} className="text-gray-500" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => decrementQuantity(index)}
                                  disabled={
                                    item.quantity === 0 || !item.product
                                  }
                                  className="w-8 h-8 text-xl font-bold text-gray-700 disabled:opacity-40 flex items-center justify-center"
                                >
                                  −
                                </button>
                              )}

                              {/* Quantity Display - Read-only */}
                              <div className="w-8 h-8 flex items-center justify-center text-base font-medium bg-white text-gray-900">
                                {item.quantity}
                              </div>

                              {/* Plus Button */}
                              <button
                                type="button"
                                onClick={() => incrementQuantity(index)}
                                disabled={
                                  !item.product ||
                                  item.quantity >=
                                    (findProductById(item.product)
                                      ?.quantity || 999)
                                }
                                className="w-8 h-8 text-xl font-bold text-primary-text disabled:opacity-40 flex items-center justify-center"
                              >
                                +
                              </button>
                            </div>
                            
                            {(() => {
                              const product = findProductById(item.product);
                              return product?.subscription_option === "Yes" && (
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleSubscribe(item)}
                                      disabled={!item.product}
                                      className={`px-4 py-2 text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ${
                                        item.enable_subscription
                                          ? "bg-green-100 text-green-700"
                                          : "bg-primary-100 text-white hover:bg-primary-100/90"
                                      }`}
                                    >
                                      {item.enable_subscription
                                        ? "Subscribed"
                                        : "Subscribe"}
                                    </button>
                                    {item.enable_subscription &&
                                      item.subscription_frequency && (
                                        <span className="text-xs text-gray-600 capitalize">
                                          ({item.subscription_frequency})
                                        </span>
                                      )}
                                  </div>
                                </div>
                              );
                            })()}
                            {/* Price */}
                            <div className="flex items-center justify-end">
                              <span className="font-semibold text-gray-800">
                                $
                                {(() => {
                                  const product = findProductById(item.product);
                                  return product
                                    ? (product.price * item.quantity).toFixed(2)
                                    : "0.00";
                                })()}
                              </span>
                            </div>
                          </div>

                          {/* Row 3: Subscribe Button */}
                         

                          {/* Delete Button */}
                          {orderData.products.length > 1 && (
                            <button
                              onClick={() => removeProduct(index)}
                              className="text-primary-100 text-sm absolute bg-gray-200 rounded-full -top-2 -right-2"
                            >
                              <Trash className="h-6 w-6 p-1 border border-primary-100 rounded-full" />
                            </button>
                          )}
                        </div>

                        {/* Desktop: Grid layout */}
                        <div className="hidden md:grid md:grid-cols-12 gap-4 items-center">
                          <div className="col-span-6">
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              value={
                                productOptions
                                  .flatMap((group) => group.options)
                                  .find((opt) => opt.value === item.product) ||
                                null
                              }
                              onChange={(selectedOption) =>
                                handleProductChange(
                                  index,
                                  "product",
                                  selectedOption?.value || ""
                                )
                              }
                              onMenuOpen={() =>
                                setFocusedSelectIndex(`desktop-${index}`)
                              }
                              onMenuClose={() => setFocusedSelectIndex(null)}
                              options={productOptions}
                              placeholder={
                                focusedSelectIndex === `desktop-${index}`
                                  ? ""
                                  : "Select Product"
                              }
                              isClearable
                              isSearchable
                              name={`product-desktop-${index}`}
                              styles={customSelectStyles}
                              noOptionsMessage={() => "No products found"}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              formatOptionLabel={(option) => (
                                <div className="flex items-center gap-2">
                                  {option.showOutOfStockLabel && (
                                    <span className="text-primary-100 bg-red-100 font-semibold  text-[8px] leading-none px-1 py-1 rounded whitespace-nowrap">
                                      Out of <br/> Stock
                                    </span>
                                  )}
                                  <span className={option.isOutOfStock ? "text-gray-400" : ""}>
                                    {option.label}
                                  </span>
                                </div>
                              )}
                              isOptionDisabled={(option) => option.isOutOfStock}
                            />
                          </div>

                          <div className="col-span-2">
                            <div className="flex items-center border border-neutral-300 rounded-full overflow-hidden w-fit bg-white">
                              {/* Minus Button - Show trash icon at minimum quantity */}
                              {item.quantity ===
                              (findProductById(item.product)
                                ?.minimum_quantity || 1) ? (
                                <button
                                  type="button"
                                  onClick={() => decrementQuantity(index)}
                                  disabled={
                                    item.quantity === 0 ||
                                    !item.product ||
                                    (item.quantity ===
                                      (findProductById(item.product)
                                        ?.minimum_quantity || 1) &&
                                      orderData.products.length === 1)
                                  }
                                  className="w-8 h-8 text-xl font-bold disabled:opacity-40 flex items-center justify-center"
                                >
                                  <Trash2 size={16} className="text-gray-500" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => decrementQuantity(index)}
                                  disabled={
                                    item.quantity === 0 || !item.product
                                  }
                                  className="w-8 h-8 text-xl font-bold text-gray-700 disabled:opacity-40 flex items-center justify-center"
                                >
                                  −
                                </button>
                              )}

                              {/* Quantity Display - Read-only */}
                              <div className="w-8 h-8 flex items-center justify-center text-base font-medium bg-white text-gray-900">
                                {item.quantity}
                              </div>

                              {/* Plus Button */}
                              <button
                                type="button"
                                onClick={() => incrementQuantity(index)}
                                disabled={
                                  !item.product ||
                                  item.quantity >=
                                    (findProductById(item.product)
                                      ?.quantity || 999)
                                }
                                className="w-8 h-8 text-xl font-bold text-primary-text disabled:opacity-40 flex items-center justify-center"
                              >
                                +
                              </button>
                            </div>
                          </div>

                          <div className="flex items-center justify-between col-span-4">
                            <span className="font-semibold text-gray-800 min-w-[70px]">
                              $
                              {(() => {
                                const product = findProductById(item.product);
                                return product
                                  ? (product.price * item.quantity).toFixed(2)
                                  : "0.00";
                              })()}
                            </span>
                            {(() => {
                              const product = findProductById(item.product);
                              return product?.subscription_option === "Yes" && (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleSubscribe(item)}
                                    disabled={!item.product}
                                    className={`px-3 py-1.5 text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ${
                                      item.enable_subscription
                                        ? "bg-green-100 text-green-700"
                                        : "bg-primary-100 text-white hover:bg-primary-100/90"
                                    }`}
                                  >
                                    {item.enable_subscription
                                      ? "Subscribed"
                                      : "Subscribe"}
                                  </button>
                                  {item.enable_subscription &&
                                    item.subscription_frequency && (
                                      <span className="text-xs text-gray-600 capitalize">
                                        ({item.subscription_frequency})
                                      </span>
                                    )}
                                </div>
                              );
                            })()}
                            {orderData.products.length > 1 && (
                              <button
                                onClick={() => removeProduct(index)}
                                className="ml-2 text-primary-100 text-sm absolute bg-gray-200 rounded-full -top-2 -right-2"
                              >
                                <Trash className="h-6 w-6 p-1 border border-primary-100 rounded-full" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-end md:mr-[26%]">
                      <div className="text-right">
                        <span className="text-lg font-semibold text-primary-100">
                          Total: ${orderData.total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Custom Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes:
                </label>
                <textarea
                  value={orderData.customNotes}
                  onChange={(e) => {
                    const notes = e.target.value;
                    setOrderData((prev) => ({
                      ...prev,
                      customNotes: notes,
                    }));
                    saveCustomNotes(notes);
                  }}
                  rows="3"
                  className="w-full border-b border-gray-300 px-3 py-2 focus:outline-none resize-none"
                  placeholder="Add any special instructions or notes..."
                />
              </div>

              {/* Submit Button */}
              <div className="text-center py-5">
                <button
                  onClick={handleProceedToCheckout}
                  disabled={
                    loading ||
                    !isFormValid ||
                    !isPartnerAcceptingOrders ||
                    selectedDeliveryPartnerLoading ||
                    deliveryPartnersLoading
                  }
                  className={`w-fit px-8 py-3 main-btn text-white font-semibold rounded-full transition-all duration-200 ${
                    loading ||
                    !isFormValid ||
                    !isPartnerAcceptingOrders ||
                    selectedDeliveryPartnerLoading ||
                    deliveryPartnersLoading
                      ? "bg-gray-400 cursor-not-allowed opacity-60"
                      : "bg-primary-100 hover:bg-primary-110 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {"Processing to checkout..."}
                    </span>
                  ) : (
                    "Proceed to Checkout"
                  )}
                </button>

                {/* Partner not accepting orders message */}
                {!isPartnerAcceptingOrders && currentPartnerData && (
                  <div className="mt-3 text-center">
                    <p className="text-red-600 text-sm font-medium">
                      This partner is not able to take orders right now
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* OTP Verification Modal */}
      <OTPModal
        isOpen={isOTPModalOpen}
        onClose={() => {
          setIsOTPModalOpen(false);
          setOtpVerificationData(null);
          setCheckoutStep("initial");
        }}
        onBack={() => {
          setIsOTPModalOpen(false);
          setOtpVerificationData(null);
          setCheckoutStep("initial");
        }}
        phoneNumber={otpVerificationData?.phone_number}
        verificationData={otpVerificationData}
        onVerificationSuccess={handleOTPVerificationSuccess}
        phone_code={coreSettings?.DEFAULT_PHONE_CODE || "+1"}
      />

      {/* Subscription Modal */}
      {isSubscriptionModalOpen && selectedProductForSubscription && (
        <DeliveryPartnerSubscriptionModal
          open={isSubscriptionModalOpen}
          setOpen={setIsSubscriptionModalOpen}
          product={selectedProductForSubscription.productDetails}
          quantity={selectedProductForSubscription.quantity}
          onConfirm={handleSubscriptionConfirm}
          initialData={{
            frequency:
              selectedProductForSubscription.subscription_frequency || "weekly",
            customNotes:
              selectedProductForSubscription.subscription_custom_notes || "",
          }}
        />
      )}

      {/* Past Date Warning Modal */}
      <PastDateWarningModal
        open={isPastDateWarningOpen}
        setOpen={setIsPastDateWarningOpen}
        selectedDate={pastDateSelected}
        onProceed={() => {
          // User confirmed they want to use the past date
          const dateOption = bypassPastDateCheck;
          if (dateOption) {
            // Update Redux state
            dispatch(setSelectedDeliveryDate(dateOption));
            // Set local state directly for past dates
            setSelectedDate(dateOption.date);
            setOrderData((prev) => ({ ...prev, deliveryDate: dateOption.date }));
            // Also try to update via API (but don't block on failure)
            if (userInfo?.user_id) {
              dispatch(
                updatePickupDate({
                  user_id: userInfo.user_id,
                  pickup_date: dateOption.date,
                  pickup_time: "",
                  delivery_method: "",
                  action: "update-pickup-time",
                })
              );
            }
            setBypassPastDateCheck(null);
          }
        }}
        onCancel={() => {
          // User cancelled, reset the stored date option
          setBypassPastDateCheck(null);
        }}
      />
    </>
  );
};

export default DeliveryPartners;